/**
 * Subscription Hook
 * Manages subscription state and API interactions
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../../services/api';
import { useAuthStore } from '../../../../stores/auth-store';
import type { UseSubscriptionReturn, Subscription, SubscriptionTier, SubscriptionSource } from '../types';

export function useSubscription(): UseSubscriptionReturn {
  const { user } = useAuthStore();

  // State
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load subscription on mount
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        setIsLoading(true);
        const response = await api.getSubscription();
        setSubscription({
          tier: response.subscription.tier as SubscriptionTier,
          source: response.subscription.source as SubscriptionSource,
          status: response.subscription.status,
          current_period_end: response.subscription.current_period_end,
        });
      } catch {
        // User might not have a subscription yet
        setSubscription({
          tier: (user?.subscription_tier as SubscriptionTier) || 'free',
          source: 'none',
          status: 'active',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscription();
  }, [user?.subscription_tier]);

  // Derived values
  const currentTier: SubscriptionTier = subscription?.tier || (user?.subscription_tier as SubscriptionTier) || 'free';
  const subscriptionSource: SubscriptionSource = (subscription?.source as SubscriptionSource) || 'none';

  // Handle upgrade checkout
  const handleUpgrade = useCallback(async (tier: 'pro' | 'pro_plus') => {
    setError(null);
    setIsCheckoutLoading(true);

    try {
      const response = await api.createCheckoutSession(tier);
      window.location.href = response.url;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setIsCheckoutLoading(false);
    }
  }, []);

  // Handle manage subscription (Stripe portal)
  const handleManageSubscription = useCallback(async () => {
    setError(null);
    setIsCheckoutLoading(true);

    try {
      const response = await api.getCustomerPortal();
      window.location.href = response.url;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
      setIsCheckoutLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    subscription,
    currentTier,
    subscriptionSource,
    isLoading,
    isCheckoutLoading,
    error,
    handleUpgrade,
    handleManageSubscription,
    clearError,
  };
}
